import { Component } from '@theme/component';

/**
 * @typedef {object} ColorGroupSwatchesRefs
 * @property {HTMLElement} value - The colour name in the "Renk:" heading.
 */

/**
 * Product page colour picker: the heading previews the colour under the pointer or focus,
 * the colour's page is prefetched on intent, and the chosen card dims while it loads.
 *
 * @extends {Component<ColorGroupSwatchesRefs>}
 */
class ColorGroupSwatches extends Component {
  requiredRefs = ['value'];

  /** @type {Set<string>} */
  #prefetched = new Set();

  /** @param {Event} event */
  preview = (event) => {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;

    this.refs.value.textContent = link.dataset.colorName ?? '';
    this.#prefetch(link.href);
  };

  restore = () => {
    // Keep showing the colour that is loading.
    if (this.querySelector('.is-loading')) return;
    this.refs.value.textContent = this.dataset.currentName ?? '';
  };

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    const link = event.target;
    if (!(link instanceof HTMLAnchorElement)) return;

    // Already on this colour.
    if (link.getAttribute('aria-current') === 'page') {
      event.preventDefault();
      return;
    }

    // New tab or window: leave the page as it is.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

    link.classList.add('is-loading');
    this.refs.value.textContent = link.dataset.colorName ?? '';
  };

  /** @param {string} url */
  #prefetch(url) {
    if (this.#prefetched.has(url)) return;
    this.#prefetched.add(url);

    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = url;
    document.head.append(hint);
  }
}

if (!customElements.get('color-group-swatches')) {
  customElements.define('color-group-swatches', ColorGroupSwatches);
}

// Coming back with the browser's back button can restore a dimmed card from the page cache.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;

  for (const link of document.querySelectorAll('.pgs__link.is-loading')) {
    link.classList.remove('is-loading');
  }

  for (const swatches of document.querySelectorAll('color-group-swatches')) {
    if (swatches instanceof ColorGroupSwatches) swatches.restore();
  }
});
